import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, Upload } from 'lucide-react'
import { Badge, Can, KeyValueList, formatDateTime, notify } from '@/kit/ui'
import { LOGO_MAX_BYTES, inApp } from '@/lib/inApp'
import { CONNECTION_LABEL, PARTNER_TYPE_LABEL, lenderLine, money, statusLabel } from '@/lib/lender'
import { useCurrentUser } from '@/shell/useCurrentUser'
import { refreshLender, useHubStore } from '@/store/hubStore'
import type { Lender } from '@/types'
import { LenderLogo } from '../lenders/LenderBits'
import { ApiDrawer, BasicsDrawer, DetailsDrawer } from './LenderInfoDrawers'
import { WhereItShows } from './WhereItShows'

const WL = { confirmed: 'success', requested: 'warning', not_needed: 'gray' } as const
const code = (s: string) => (s ? <code className="break-all">{s}</code> : null)

/** The optional details and how many of them are filled in (PF-2: nothing here is needed to go live). */
export function detailsFilled(l: Lender) {
  const items = [l.legalName, l.rbiCor, l.lenders.length ? 'x' : '', l.grievance.name && l.grievance.email ? 'x' : '', l.links.privacy, l.links.grievancePolicy, l.links.fairPractices, l.appName, l.logoPermission ? 'x' : '', l.website, l.shortLine]
  return { filled: items.filter((x) => x.trim()).length, of: items.length }
}

/** Replace the logo right on the card (the same upload as Add a lender and tab 6). */
function LogoReplace({ lender }: { lender: Lender }) {
  const { can } = useCurrentUser()
  const [busy, setBusy] = useState(false)
  if (!can('app.edit') || !inApp.available()) return null
  const pick = async (file: File) => {
    if (file.size > LOGO_MAX_BYTES) return notify.warning('That logo is too big', `${Math.round(LOGO_MAX_BYTES / 1024)} KB at most.`)
    setBusy(true)
    try {
      await inApp.uploadLogo(lender.slug, file)
      await refreshLender(lender.slug)
      notify.success('Logo uploaded')
    } catch (err) {
      notify.error('Logo not uploaded', err instanceof Error ? err.message : String(err))
    } finally { setBusy(false) }
  }
  return (
    <label className={`btn btn-secondary btn-sm ${busy ? 'pointer-events-none opacity-60' : ''}`}>
      {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} {lender.hasLogo ? 'Replace logo' : 'Upload logo'}
      <input type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" disabled={busy} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void pick(f) }} />
    </label>
  )
}

/**
 * Tab 1 · Lender info (PF-2, owner 2026-10-19: "simple information … api details later"): three cards, each with its
 * own small Edit drawer. Basics = what Add a lender asked; Optional details = the legal side, never needed to go live;
 * API = filled in when the lender gives an API. Every save writes one History line naming the fields.
 */
export function InfoTab({ lender: l }: { lender: Lender }) {
  const [open, setOpen] = useState<'basics' | 'details' | 'api' | null>(null)
  const allHistory = useHubStore((s) => s.history)
  const history = useMemo(() => allHistory.filter((h) => h.lenderSlug === l.slug && h.where === 'Lender info').slice(0, 3), [allHistory, l.slug])
  const p = l.products[0]
  const f = p?.keyFacts
  const details = detailsFilled(l)
  const liveEndpoints = l.workflows.filter((w) => w.state === 'live').length
  const edit = (which: typeof open, label: string) => (
    <Can permission="lenders.edit"><button type="button" className="btn btn-secondary btn-sm" onClick={() => setOpen(which)}>{label}</button></Can>
  )

  return (
    <>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <section className="card lg:row-span-2" aria-label="Basics">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h2 className="section-title !mb-0">Basics</h2>
            {edit('basics', 'Edit basics')}
          </div>
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <LenderLogo lender={l} size={44} />
            <div className="min-w-0"><b className="block">{l.name}</b><span className="text-2xs text-muted">{lenderLine(l)}</span></div>
            <LogoReplace lender={l} />
          </div>
          <KeyValueList
            items={[
              { label: 'Type', value: PARTNER_TYPE_LABEL[l.partnerType] },
              { label: 'Status', value: statusLabel(l) },
              { label: 'UTM link', value: code(l.links.web) },
              { label: 'Extra link parameters', value: Object.keys(l.linkParams ?? {}).length ? code(Object.entries(l.linkParams).map(([k, v]) => `${k}=${v}`).join('&')) : null },
              { label: 'Play Store', value: code(l.links.play) },
              { label: 'App Store', value: code(l.links.appstore) },
              { label: p ? `Loan information (${p.name})` : 'Loan information', value: f ? [f.amountMax > 0 && `up to ${money(f.amountMax)}`, f.rateMinPa > 0 && `from ${f.rateMinPa}% a year`, f.tenureMaxMonths > 0 && `up to ${f.tenureMaxMonths} months`, f.processingFee && `fee ${f.processingFee}`].filter(Boolean).join(' · ') || null : null },
              { label: 'Features', value: f?.extra.length ? f.extra.join(' · ') : null },
            ]}
          />
          <p className="mt-2 text-2xs text-muted">Every product and its full key facts: tab <Link to={`/lenders/${l.slug}/products`}>2 · Products</Link>.</p>
        </section>

        <section className="card" aria-label="Optional details">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h2 className="section-title !mb-0">Optional details <span className="font-normal text-muted">({details.filled} of {details.of} filled)</span></h2>
            {edit('details', 'Edit details')}
          </div>
          <KeyValueList
            items={[
              { label: 'Legal name', value: l.legalName || null },
              { label: 'RBI registration (CoR)', value: l.rbiCor || null },
              { label: 'Who lends', value: l.lenders.length ? <ul className="space-y-0.5">{l.lenders.map((r, i) => <li key={i}>{r.legalName} · {r.kind === 'bank' ? 'Bank' : `NBFC${r.rbiCor ? ` ${r.rbiCor}` : ''}`}</li>)}</ul> : null },
              { label: 'Digital lending app', value: l.appName || null },
              { label: 'Grievance officer', value: l.grievance.name ? [l.grievance.name, l.grievance.designation, l.grievance.email, l.grievance.phone, l.grievance.hours].filter(Boolean).join(' · ') : null },
              { label: 'Grievance address', value: l.grievance.address || null },
              { label: 'Privacy policy', value: code(l.links.privacy) },
              { label: 'Grievance policy', value: code(l.links.grievancePolicy) },
              { label: 'Fair Practices Code', value: code(l.links.fairPractices) },
              { label: 'Logo permission', value: l.logoPermission ? `given ${l.logoPermission.date}${l.logoPermission.note ? ` · ${l.logoPermission.note}` : ''}` : null },
              { label: 'Website', value: code(l.website) },
              { label: 'Short line', value: l.shortLine ? `“${l.shortLine}”` : null },
            ]}
          />
          <p className="mt-2 text-2xs text-muted">None of these is needed to go live; the website and Apply now need some before Publish (below).</p>
        </section>

        <section className="card" aria-label="API">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h2 className="section-title !mb-0">API <span className="font-normal text-muted">({l.connection === 'api' ? `connected · ${liveEndpoints} live endpoint${liveEndpoints === 1 ? '' : 's'}` : l.connection === 'manual' ? 'manual: our team enters the leads on their portal' : 'link only'})</span></h2>
            {edit('api', 'Edit API')}
          </div>
          <KeyValueList
            items={[
              { label: 'How we connect', value: CONNECTION_LABEL[l.connection] },
              { label: 'Credit-check note', value: l.creditCheckNote || null },
              { label: 'UAT base URL', value: code(l.env.uatBaseUrl) },
              { label: 'Production base URL', value: code(l.env.prodBaseUrl) },
              { label: 'Keys', value: l.envVars.length ? <span>{l.envVars.length} in tab <Link to={`/lenders/${l.slug}/env`}>5 · Env variables</Link>{l.envVars.some((v) => !v.uat) ? <> · <Badge tone="warning">{l.envVars.filter((v) => !v.uat).length} without a UAT value</Badge></> : null}</span> : null },
              { label: 'Our outgoing IPs', value: <code>UAT 13.233.xx.xx · Prod 3.109.xx.xx</code> },
              { label: 'IP whitelist at the lender', value: <Badge tone={WL[l.ipWhitelist.state]}>{l.ipWhitelist.state.replace('_', ' ')}{l.ipWhitelist.date ? ` ${l.ipWhitelist.date}` : ''}</Badge> },
            ]}
          />
          <p className="mt-2 text-2xs text-muted">Filled in when the lender gives us an API; the code and workflows are in tab 3. A link-only lender needs none of it.</p>
        </section>
      </div>

      <div className="mt-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-2xs text-muted dark:border-slate-700 dark:bg-slate-900">
        <b className="text-default">Modified</b>
        {history.length ? history.map((h) => <div key={h.id}>{formatDateTime(h.at)} · {h.who} · {h.what}</div>) : <div>No change since it was created ({formatDateTime(l.createdAt)}).</div>}
      </div>

      <WhereItShows lender={l} />

      <BasicsDrawer lender={l} isOpen={open === 'basics'} onClose={() => setOpen(null)} />
      <DetailsDrawer lender={l} isOpen={open === 'details'} onClose={() => setOpen(null)} />
      <ApiDrawer lender={l} isOpen={open === 'api'} onClose={() => setOpen(null)} />
    </>
  )
}
